// ChatV2Tester.jsx
// Console tool for exercising the chatV2 backend

import React, { useMemo, useState, useEffect } from "react";
import { auth } from "../../Firebase";
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

// CSS
import "../SpeciesInfoUpload/PlantEditor.css";

const DEFAULT_API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://us-central1-plants-5f51e.cloudfunctions.net/api";

const ChatV2Tester = () => {
  const [apiBase, setApiBase] = useState(DEFAULT_API_BASE);
  const [task, setTask] = useState("plant-chat");
  const [stream, setStream] = useState(false);

  const [context, setContext] = useState({
    userName: "Console User",
    leaflingName: "",
    species: "",
    plantLocation: "",
    userNotes: "",
    otherNotes: "",
    environmentNotes: "",
  });
  const [plantsonalityJson, setPlantsonalityJson] = useState("");
  const [logsText, setLogsText] = useState("");

  const [messageHistory, setMessageHistory] = useState([
    {
      id: "msg-1",
      role: "user",
      content: "Hey, how are you feeling today?",
      imageUrlsText: "",
    },
  ]);

  const [identifyImageUrlsText, setIdentifyImageUrlsText] = useState("");
  const [identifyNotes, setIdentifyNotes] = useState("");
  const [speciesName, setSpeciesName] = useState("");

  const [loading, setLoading] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [rawResponse, setRawResponse] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (task !== "plant-chat") {
      setStream(false);
    }
  }, [task]);

  const sectionStyle = {
    padding: "16px",
    border: "1px solid #e2e2e2",
    borderRadius: "8px",
    marginBottom: "16px",
    backgroundColor: "#fff",
  };

  const labelStyle = { fontWeight: "bold", display: "block" };
  const inputStyle = {
    width: "100%",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  };
  const textAreaStyle = {
    width: "100%",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    minHeight: "90px",
  };

  const splitUrls = (text) =>
    text
      .split(/[\n,]+/)
      .map((value) => value.trim())
      .filter(Boolean);

  const compactObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => {
        if (value === null || value === undefined) return false;
        if (typeof value === "string") return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        return true;
      })
    );

  const normalizeMessages = () =>
    messageHistory
      .map((message) => {
        const content = message.content || "";
        const imageUrls = splitUrls(message.imageUrlsText || "");
        if (!content.trim() && imageUrls.length === 0) {
          return null;
        }
        const payload = {
          role: message.role,
          content,
        };
        if (imageUrls.length > 0) {
          payload.imageUrls = imageUrls;
        }
        return payload;
      })
      .filter(Boolean);

  const buildRequestBody = () => {
    const body = { task };

    if (task === "plant-chat") {
      let plantsonality;
      if (plantsonalityJson.trim()) {
        plantsonality = JSON.parse(plantsonalityJson);
      }

      const logs = logsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      body.context = compactObject({
        ...context,
        plantsonality,
        logs,
      });
      body.messageHistory = normalizeMessages();
      if (stream) {
        body.stream = true;
      }
    }

    if (task === "identify-plant") {
      body.imageUrls = splitUrls(identifyImageUrlsText);
      body.context = compactObject({ notes: identifyNotes });
    }

    if (task === "species-info") {
      body.context = compactObject({ speciesName: speciesName.trim() });
    }

    return body;
  };

  const validateRequest = (body) => {
    if (task === "plant-chat") {
      if (!body.context?.species) {
        throw new Error("Plant chat requires context.species");
      }
      if (!body.messageHistory || body.messageHistory.length === 0) {
        throw new Error("Plant chat requires at least one message");
      }
    }

    if (task === "identify-plant") {
      if (!body.imageUrls || body.imageUrls.length === 0) {
        throw new Error("Identify requires at least one image URL");
      }
    }

    if (task === "species-info") {
      if (!body.context?.speciesName) {
        throw new Error("Species info requires context.speciesName");
      }
    }
  };

  const getIdToken = async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No authenticated user found");
    }
    return user.getIdToken();
  };

  const setResultState = (data) => {
    setResponseData(data);
    setRawResponse(JSON.stringify(data, null, 2));
  };

  const handleSend = async () => {
    setError("");
    setResponseData(null);
    setRawResponse("");
    setStreamText("");
    setLoading(true);

    try {
      const body = buildRequestBody();
      validateRequest(body);

      if (stream && task !== "plant-chat") {
        throw new Error("Streaming is only supported for plant-chat");
      }

      const token = await getIdToken();
      const url = `${apiBase.replace(/\/$/, "")}/chatV2`;

      if (stream) {
        await streamResponse(url, body, token);
      } else {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Request failed");
        }

        const data = await response.json();
        setResultState(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const streamResponse = async (url, body, token) => {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Request failed");
    }

    if (!response.body) {
      throw new Error("Streaming not supported by this browser");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let accumulated = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split("\n\n");
      buffer = events.pop() || "";

      for (const eventChunk of events) {
        const lines = eventChunk.split("\n");
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payloadText = line.replace(/^data:\s*/, "");
          if (!payloadText) continue;
          const payload = JSON.parse(payloadText);

          if (payload.delta) {
            accumulated += payload.delta;
            setStreamText(accumulated);
          }

          if (payload.error) {
            throw new Error(payload.error);
          }

          if (payload.done) {
            setResultState(payload);
          }
        }
      }
    }
  };

  const addMessage = () => {
    setMessageHistory((prev) => [
      ...prev,
      {
        id: `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        role: "user",
        content: "",
        imageUrlsText: "",
      },
    ]);
  };

  const updateMessage = (id, updates) => {
    setMessageHistory((prev) =>
      prev.map((message) =>
        message.id === id ? { ...message, ...updates } : message
      )
    );
  };

  const removeMessage = (id) => {
    setMessageHistory((prev) => prev.filter((message) => message.id !== id));
  };

  const resetResponse = () => {
    setResponseData(null);
    setRawResponse("");
    setStreamText("");
    setError("");
  };

  const loadSample = () => {
    if (task === "plant-chat") {
      setContext((prev) => ({
        ...prev,
        species: "Pilea peperomioides",
        leaflingName: "Penny",
        userName: "Console User",
      }));
      setMessageHistory([
        {
          id: "msg-1",
          role: "user",
          content: "Any tips for brighter leaves?",
          imageUrlsText: "",
        },
      ]);
      setPlantsonalityJson(
        JSON.stringify(
          {
            traits: { humor: 3, chaos: 1, evil: 1, sass: 2, charm: 4 },
          },
          null,
          2
        )
      );
    }

    if (task === "identify-plant") {
      setIdentifyImageUrlsText("https://example.com/plant.jpg");
      setIdentifyNotes("Leafy plant with round leaves, indoor houseplant.");
    }

    if (task === "species-info") {
      setSpeciesName("Dracaena trifasciata");
    }
  };

  const { requestPreview, previewError } = useMemo(() => {
    try {
      const body = buildRequestBody();
      return { requestPreview: JSON.stringify(body, null, 2), previewError: "" };
    } catch (err) {
      return { requestPreview: "", previewError: err.message };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    task,
    stream,
    context,
    plantsonalityJson,
    logsText,
    messageHistory,
    identifyImageUrlsText,
    identifyNotes,
    speciesName,
  ]);

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">Chat V2 Tester</div>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "20px" }}>
        <div style={sectionStyle}>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <label style={labelStyle}>API Base</label>
              <input
                style={inputStyle}
                value={apiBase}
                onChange={(e) => setApiBase(e.target.value)}
              />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label style={labelStyle}>Task</label>
              <select
                style={inputStyle}
                value={task}
                onChange={(e) => setTask(e.target.value)}
              >
                <option value="plant-chat">plant-chat</option>
                <option value="identify-plant">identify-plant</option>
                <option value="species-info">species-info</option>
              </select>
            </div>
            <div style={{ flex: "0 0 150px" }}>
              <label style={labelStyle}>Stream</label>
              <div>
                <input
                  type="checkbox"
                  checked={stream}
                  disabled={task !== "plant-chat"}
                  onChange={(e) => setStream(e.target.checked)}
                />{" "}
                Enable
              </div>
              {task !== "plant-chat" && (
                <div style={{ fontSize: "12px", color: "#666" }}>
                  Streaming is only available for plant-chat.
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: "12px" }}>
            <button
              onClick={loadSample}
              style={{
                padding: "8px 14px",
                backgroundColor: "#f0f0f0",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
                marginRight: "10px",
              }}
            >
              Load Sample
            </button>
            <button
              onClick={resetResponse}
              style={{
                padding: "8px 14px",
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Clear Response
            </button>
          </div>
        </div>

        {task === "plant-chat" && (
          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Plant Chat Context</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>User Name</label>
                <input
                  style={inputStyle}
                  value={context.userName}
                  onChange={(e) =>
                    setContext((prev) => ({
                      ...prev,
                      userName: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label style={labelStyle}>Leafling Name</label>
                <input
                  style={inputStyle}
                  value={context.leaflingName}
                  onChange={(e) =>
                    setContext((prev) => ({
                      ...prev,
                      leaflingName: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label style={labelStyle}>Species</label>
                <input
                  style={inputStyle}
                  value={context.species}
                  onChange={(e) =>
                    setContext((prev) => ({
                      ...prev,
                      species: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label style={labelStyle}>Plant Location</label>
                <input
                  style={inputStyle}
                  value={context.plantLocation}
                  onChange={(e) =>
                    setContext((prev) => ({
                      ...prev,
                      plantLocation: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label style={labelStyle}>User Notes</label>
                <input
                  style={inputStyle}
                  value={context.userNotes}
                  onChange={(e) =>
                    setContext((prev) => ({
                      ...prev,
                      userNotes: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label style={labelStyle}>Other Notes</label>
                <input
                  style={inputStyle}
                  value={context.otherNotes}
                  onChange={(e) =>
                    setContext((prev) => ({
                      ...prev,
                      otherNotes: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label style={labelStyle}>Environment Notes</label>
                <input
                  style={inputStyle}
                  value={context.environmentNotes}
                  onChange={(e) =>
                    setContext((prev) => ({
                      ...prev,
                      environmentNotes: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>Plantsonality (JSON)</label>
              <textarea
                style={textAreaStyle}
                value={plantsonalityJson}
                onChange={(e) => setPlantsonalityJson(e.target.value)}
                placeholder='{"traits": {"humor": 3, "chaos": 1, "evil": 1, "sass": 2, "charm": 4}}'
              />
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>Logs (one per line)</label>
              <textarea
                style={textAreaStyle}
                value={logsText}
                onChange={(e) => setLogsText(e.target.value)}
              />
            </div>
          </div>
        )}

        {task === "plant-chat" && (
          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Message History</h3>
            {messageHistory.map((message) => (
              <div
                key={message.id}
                style={{
                  padding: "12px",
                  border: "1px solid #e2e2e2",
                  borderRadius: "6px",
                  marginBottom: "12px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  <select
                    style={{ ...inputStyle, maxWidth: "160px" }}
                    value={message.role}
                    onChange={(e) =>
                      updateMessage(message.id, { role: e.target.value })
                    }
                  >
                    <option value="user">user</option>
                    <option value="assistant">assistant</option>
                    <option value="system">system</option>
                  </select>
                  <button
                    onClick={() => removeMessage(message.id)}
                    style={{
                      padding: "6px 10px",
                      backgroundColor: "#ffecec",
                      border: "1px solid #f2b6b6",
                      borderRadius: "4px",
                      cursor: "pointer",
                      marginLeft: "auto",
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div style={{ marginTop: "10px" }}>
                  <label style={labelStyle}>Content</label>
                  <textarea
                    style={textAreaStyle}
                    value={message.content}
                    onChange={(e) =>
                      updateMessage(message.id, { content: e.target.value })
                    }
                  />
                </div>

                <div style={{ marginTop: "10px" }}>
                  <label style={labelStyle}>
                    Image URLs (one per line or comma-separated)
                  </label>
                  <textarea
                    style={{ ...textAreaStyle, minHeight: "70px" }}
                    value={message.imageUrlsText}
                    onChange={(e) =>
                      updateMessage(message.id, {
                        imageUrlsText: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            ))}
            <button
              onClick={addMessage}
              style={{
                padding: "8px 14px",
                backgroundColor: "#f0f0f0",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Add Message
            </button>
          </div>
        )}

        {task === "identify-plant" && (
          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Identify Plant Inputs</h3>
            <label style={labelStyle}>Image URLs</label>
            <textarea
              style={textAreaStyle}
              value={identifyImageUrlsText}
              onChange={(e) => setIdentifyImageUrlsText(e.target.value)}
              placeholder="https://..."
            />
            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>Notes (optional)</label>
              <textarea
                style={textAreaStyle}
                value={identifyNotes}
                onChange={(e) => setIdentifyNotes(e.target.value)}
              />
            </div>
          </div>
        )}

        {task === "species-info" && (
          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Species Info Input</h3>
            <label style={labelStyle}>Species Name</label>
            <input
              style={inputStyle}
              value={speciesName}
              onChange={(e) => setSpeciesName(e.target.value)}
              placeholder="Dracaena trifasciata"
            />
          </div>
        )}

        <div style={sectionStyle}>
          <h3 style={{ marginTop: 0 }}>Request Preview</h3>
          {previewError && (
            <div style={{ color: "#c62828", marginBottom: "10px" }}>
              {previewError}
            </div>
          )}
          <pre
            style={{
              backgroundColor: "#f6f6f6",
              padding: "12px",
              borderRadius: "6px",
              overflowX: "auto",
              minHeight: "120px",
            }}
          >
            {requestPreview || "Build a request to preview the payload."}
          </pre>

          <button
            onClick={handleSend}
            disabled={loading}
            style={{
              padding: "10px 16px",
              backgroundColor: loading ? "#ccc" : "#2e7d32",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Sending..." : "Send Request"}
          </button>
        </div>

        <div style={sectionStyle}>
          <h3 style={{ marginTop: 0 }}>Response</h3>
          {error && (
            <div
              style={{
                padding: "10px",
                backgroundColor: "#ffebee",
                border: "1px solid #ffcdd2",
                borderRadius: "6px",
                color: "#c62828",
                marginBottom: "12px",
              }}
            >
              {error}
            </div>
          )}

          {streamText && (
            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Stream Output</label>
              <pre
                style={{
                  backgroundColor: "#f6f6f6",
                  padding: "12px",
                  borderRadius: "6px",
                  overflowX: "auto",
                  minHeight: "80px",
                }}
              >
                {streamText}
              </pre>
            </div>
          )}

          <label style={labelStyle}>Response JSON</label>
          <pre
            style={{
              backgroundColor: "#f6f6f6",
              padding: "12px",
              borderRadius: "6px",
              overflowX: "auto",
              minHeight: "120px",
            }}
          >
            {rawResponse || "No response yet."}
          </pre>
        </div>

        {responseData?.systemPrompt && (
          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>System Prompt</h3>
            <pre
              style={{
                backgroundColor: "#f6f6f6",
                padding: "12px",
                borderRadius: "6px",
                overflowX: "auto",
                minHeight: "120px",
              }}
            >
              {responseData.systemPrompt}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatV2Tester;
